/**
 * A panel floating over the page, opened and closed by a `floating-panel-toggle`
 * window event carrying its name, and dragged around by whatever calls
 * `startDrag()`.
 *
 * It first opens under the button that asked for it. Once dragged it stays
 * where it was left, on every page, until dragged again, but is always pulled
 * back into view on a smaller window.
 *
 * Opening and closing is announced as `floating-panel-changed`, so the button
 * can show whether its panel is open.
 */
export default function (name) {
    return {
        name,
        open: false,
        panel: null,
        anchor: null,
        position: null,
        placed: this.$persist(null).as(`floating-panel.${name}`),

        toggle(anchor = null) {
            this.open ? this.close() : this.show(anchor);
        },

        show(anchor = null) {
            this.anchor = anchor;
            this.position = this.placed ?? this.underAnchor();
            this.open = true;

            this.$nextTick(() => this.keepInView());
            this.$dispatch('floating-panel-changed', {name: this.name, open: true});
        },

        close() {
            this.open = false;

            this.$dispatch('floating-panel-changed', {name: this.name, open: false});
        },

        underAnchor() {
            const width = this.panel?.offsetWidth ?? 512;

            if (! this.anchor) {
                return {x: window.innerWidth - width - 24, y: 96};
            }

            return {x: this.anchor.right - width, y: this.anchor.bottom + 8};
        },

        startDrag(event) {
            if (event.button !== 0 || ! this.panel) {
                return;
            }

            const rect = this.panel.getBoundingClientRect();
            const offsetX = event.clientX - rect.left;
            const offsetY = event.clientY - rect.top;

            const move = (moveEvent) => {
                this.position = this.clamp(moveEvent.clientX - offsetX, moveEvent.clientY - offsetY);
            };

            const stop = () => {
                this.placed = this.position;

                window.removeEventListener('pointermove', move);
                window.removeEventListener('pointerup', stop);
            };

            event.preventDefault();

            window.addEventListener('pointermove', move);
            window.addEventListener('pointerup', stop);
        },

        /**
         * Keeps the panel wholly on screen across, and its header on screen
         * down, so there is always something to drag it back by.
         */
        clamp(x, y) {
            const margin = 8;
            const width = this.panel?.offsetWidth ?? 0;

            return {
                x: Math.round(Math.min(Math.max(margin, x), window.innerWidth - width - margin)),
                y: Math.round(Math.min(Math.max(margin, y), window.innerHeight - 64)),
            };
        },

        keepInView() {
            if (! this.open || ! this.position) {
                return;
            }

            this.position = this.clamp(this.position.x, this.position.y);
        },

        /**
         * Placed where it was left, and no taller than the room under it, so
         * a panel dragged low scrolls inside rather than off the screen.
         */
        style() {
            if (! this.position) {
                return {};
            }

            return {
                left: `${this.position.x}px`,
                top: `${this.position.y}px`,
                maxHeight: `min(70vh, ${Math.max(160, window.innerHeight - this.position.y - 8)}px)`,
            };
        },
    };
}
